import { useState } from 'react';
import { Plus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { formatAccounting } from '@/lib/currency';
import { useJobsByLead } from '../hooks/useJobsByLead';
import { CreateJobDialog } from './CreateJobDialog';
import type { Lead } from '@/features/leads/types';

interface LeadJobsPanelProps {
  lead: Lead;
}

/**
 * Compact list of the jobs attached to a lead.
 *
 * Previously a full DataListCard — card padding, a large heading row, column
 * headings and a data row — totalling 172px on a page that has to fit in 938px
 * and was overflowing by 702px. The legacy system has no equivalent panel at
 * all, so every pixel here is pixels the old screen did not spend.
 *
 * Rendered as one line per job rather than collapsed: hiding it would make a
 * lead with a job look identical to one without, which matters more than the
 * remaining pixels. Money values carry inline labels so dropping the column
 * headings does not make them ambiguous.
 */
export function LeadJobsPanel({ lead }: LeadJobsPanelProps) {
  const { data: jobs, isLoading, error, refetch } = useJobsByLead(lead.id);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const canCreateJob = lead.status === 'Sold';

  return (
    <div className="bg-card rounded-lg px-5 py-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-secondary">Jobs</h3>
          {!isLoading && <Badge variant="secondary">{jobs.length}</Badge>}
        </div>
        {canCreateJob && (
          <Button
            variant="outline"
            className="flex h-6 items-center gap-1.5 px-2 text-xs font-medium"
            onClick={() => setIsCreateDialogOpen(true)}
          >
            <Plus className="h-3.5 w-3.5 text-[#DC2626]" />
            Create Job
          </Button>
        )}
      </div>

      <ErrorAlert error={error} className="mt-1" />

      {isLoading ? (
        <p className="mt-1 text-xs text-muted-foreground">Loading jobs...</p>
      ) : jobs.length === 0 ? (
        <p className="mt-1 text-xs text-muted-foreground">
          No jobs created for this lead yet.
        </p>
      ) : (
        <ul className="mt-1 space-y-0.5">
          {jobs.map((job) => (
            <li
              key={job.id}
              className="flex items-center gap-4 text-xs"
            >
              <span className="font-semibold">{job.jobNumber}</span>
              <span>{job.status}</span>
              <span className="flex-1 truncate text-muted-foreground" title={job.propertyAddress}>
                {job.propertyAddress}
              </span>
              <span className="text-muted-foreground">{job.assignedTo ?? '—'}</span>
              <span className="tabular-nums">
                <span className="text-muted-foreground">Contract </span>
                {formatAccounting(job.totalCharges)}
              </span>
              <span className="tabular-nums">
                <span className="text-muted-foreground">Profit </span>
                {formatAccounting(job.profit)}
              </span>
              <a
                href={`/jobs/${job.id}/cost`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-secondary hover:underline"
              >
                View
              </a>
            </li>
          ))}
        </ul>
      )}

      <CreateJobDialog
        lead={lead}
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        onJobCreated={refetch}
      />
    </div>
  );
}
